import React, { useRef, useState } from "react";
import "./Login.css";
import ApiName from "../ApiName";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import sareLogo from "../Assets/img/SareLogo.png";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff, Lock, User } from "lucide-react";

const LoginWrapper = () => {
  const navigate = useNavigate();
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (event) => {
    event.preventDefault(); // This makes "Enter" key work perfectly inside a form!
    
    const credentials = {
      username: usernameRef.current.value,
      password: passwordRef.current.value
    };

    setIsLoading(true);

    try {
      const response = await axios.post(`${ApiName}/Auth`, credentials, {
        headers: {
          "Content-Type": "application/json"
        }
      });
      localStorage.authToken = response.data;
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (error) {
      toast.error("Invalid username or password!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center w-full min-h-screen bg-gray-50 dark:bg-gray-900 bgpattern">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md px-6"
      >
        <form 
          onSubmit={handleLogin}
          className="relative px-10 py-12 overflow-hidden bg-white shadow-2xl rounded-2xl dark:bg-zinc-900 ring-1 ring-gray-200 dark:ring-zinc-800"
        >
          {/* Decorative background blur */}
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 dark:from-indigo-900/30 dark:via-purple-900/30 dark:to-pink-900/30 opacity-70 blur-2xl"></div>

          <div className="relative z-10 flex flex-col items-center mb-8">
            <div className="flex items-center justify-center w-20 h-20 mb-4 bg-white rounded-full shadow-md dark:bg-zinc-800">
              <img src={sareLogo} alt="Sare Logo" className="w-12 drop-shadow-sm" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Portal</h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Sign in to manage your content</p>
          </div>

          <div className="relative z-10 space-y-5">
            <div>
              <label
                htmlFor="usernameInput"
                className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <User className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="usernameInput"
                  ref={usernameRef}
                  disabled={isLoading}
                  className="block w-full py-3 pl-10 pr-3 text-sm text-gray-900 transition-colors bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-zinc-800 dark:border-zinc-700 dark:text-white dark:focus:ring-indigo-500 disabled:opacity-50"
                  placeholder="Enter your username"
                  required
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="passwordInput"
                className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Lock className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="passwordInput"
                  ref={passwordRef}
                  disabled={isLoading}
                  className="block w-full py-3 pl-10 pr-10 text-sm text-gray-900 transition-colors bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-zinc-800 dark:border-zinc-700 dark:text-white dark:focus:ring-indigo-500 disabled:opacity-50"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center justify-center w-full py-3 mt-6 text-sm font-medium text-white transition-all bg-indigo-600 shadow-md rounded-xl hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-500/50 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default LoginWrapper;
